import { AfterViewInit, ChangeDetectorRef, Directive, Input, OnDestroy, OnInit, ViewRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NexusApiService } from '@df/api/nexus/nexus-api.service';
import { IRequestCallback, IRequestCallbackFunction, IResponseMeta } from '@df/api/request/request.service';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { CheckoutService } from '@df/checkout/checkout.service';
import { App } from '@df/core/app';
import { routeOverride } from '@df/core/meta/route-override.pipe';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { Logger } from '@df/core/service/logger';
import { Status } from '@df/core/status';
import { isInViewport } from '@df/dom/fn/is-in-viewport';
import { IScrollToOptions, ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { OnChange } from '@df/ng/on-change';
import { TimeoutService } from '@df/ng/timeout.service';
import { IAddressData } from '@df/session/api/address-book/address';
import { Basket } from '@df/session/api/basket/basket';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import { Customer } from '@df/session/api/customer';
import { nilMergeDeep } from '@df/util/fn/nil-merge-deep';
import { Nexus } from '@tomandco/nexus-api-types';
import clone from 'lodash-es/clone';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import findIndex from 'lodash-es/findIndex';
import first from 'lodash-es/first';
import flatten from 'lodash-es/flatten';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import last from 'lodash-es/last';
import mergeWith from 'lodash-es/mergeWith';
import omit from 'lodash-es/omit';
import sortBy from 'lodash-es/sortBy';
import union from 'lodash-es/union';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { NCheckout } from './df-checkout.config';

@Directive()
export abstract class CheckoutFormComponentDef
    implements OnInit, OnDestroy, AfterViewInit, Checkout.ICheckoutFormComponent, Checkout.ICheckoutForPaymentComponent
{
    //////////////////////////////////
    //
    // SERVICES
    //
    //////////////////////////////////

    readonly client = inject(Client);

    readonly hashStorageService = inject(HashStorageService);

    /**
     * Location service
     */
    protected router = inject(Router);

    /**
     * Logger service
     */
    protected logger = inject(Logger);

    /**
     * Events service
     */
    protected events = inject(Events);

    protected checkout = inject(CheckoutService);

    protected dfEventService = inject(DfEventService);
    protected timeoutService = inject(TimeoutService);
    protected nexusApiService: NexusApiService;
    protected _ref = inject(ChangeDetectorRef);
    readonly app = inject(App);
    protected scrollToService = inject(ScrollToService);
    private moduleConfig = inject(NCheckout.OPTIONS);
    customer = inject(Customer);
    basket = inject(Basket);

    /**
     * Customer address book shorthand
     */
    // addressBook = this.app.customer.addressBook;

    /**
     * Last data from backend
     * @see backendData getter/setteer
     * @type {Checkout.IData}
     */
    protected _backendData!: Checkout.IData;

    /**
     * Checkout data model - FE one edited
     * @type {Status}
     */
    data!: Checkout.IQuoteData;

    /**
     * Copy of data model for change detection
     * @type {Status}
     */
    lastData?: Checkout.IQuoteData;

    @OnChange(function (this: CheckoutFormComponentDef) {
        this.detectChanges();
    })
    dataError?: Checkout.IDataError;

    /**
     * Payment methods cache
     */
    protected _paymentMethodsAndTokens?: Checkout.IPaymentMethod[];

    /**
     * Just infor about component being destroyed for async things
     */
    protected destroyed = false;

    /**
     * If 3d secure needs confirmation then we will show extra step
     * (see checkout-confirmation) and we will prevent abandoning it
     *
     * This here is confirmation step name if lock is set
     *
     * @type {[type]}
     */
    protected _confirmationLock?: Checkout.IStep;

    /**
     * Non Collection Shipping Methods cache
     */
    protected _nonCollectionShippingMethods?: Checkout.IShippingMethod[];

    /**
     * Default checkout data object
     */
    protected _defaults: Checkout.IQuoteDataPartial;

    get defaults(): Checkout.IQuoteDataPartial {
        return this._defaults;
    }

    //////////////////////////////////
    //
    // PROCESSING
    //
    //////////////////////////////////

    /**
     * Status object for saving shipping address
     */
    saveShippingAddressStatus = new Status();

    /**
     * Status object for saving shipping method
     */
    saveShippingMethodStatus = new Status();

    /**
     * Status object for saving custom data
     */
    saveCustomDataStatus = new Status();

    /**
     * Status object for saving billing address
     */
    saveBillingAddressStatus = new Status();

    /**
     * Status object for saving payment method
     */
    savePaymentMethodStatus = new Status();

    /**
     * Status object for saving payment method
     */
    saveEmailStatus = new Status();

    /**
     * Status object for reset shipping method
     */
    resetShippingMethodStatus = new Status();

    /**
     * Status object for checkout submission
     */
    status = new Status();

    /**
     * Subject for checkout changes
     */
    subject = new Subject<Checkout.IStateChange>();

    checkoutBasketData$ = new BehaviorSubject<Nexus.Lib.Basket.BasketData>(this.basket.data);

    readonly checkoutBasket = clone(this.basket);

    readonly formDataChange$ = new Subject<{ step: Checkout.EStepName; change: any }>();

    private lastShippginMethodNotified?: string;
    private lastPaymentMethodNotified?: string;

    protected triggers: Record<
        string,
        {
            fields: string[];
            steps?: Checkout.EStepName[];
            cb: IRequestCallbackFunction;
        }
    > = {
        email: {
            fields: [],
            steps: [Checkout.EStepName.account],
            cb: () => this.saveEmail()
        },
        shippingAddress: {
            fields: [
                'address.shipping.country_id',
                'address.shipping.postcode',
                'address.shipping.customer_address_id',
                'address.shipping.region_id'
            ],
            cb: () => this.saveShippingAddress()
        },
        billingAddress: {
            fields: [
                'address.billing.country_id',
                'address.billing.customer_address_id',
                'address.billing.postcode',
                'address.billing.region_id',
                'billing.to_shipping'
            ],
            cb: () => this.saveBillingAddress()
        },
        shippingMethod: {
            fields: [
                'shipping.method', // shipping method
                'shipping.additionalData.store_id', // c&c store id
                'shipping.additionalData.booking_code' // c&c booking code
            ],
            cb: () => this.saveShippingMethod()
        },
        custom: {
            fields: ['custom.delivery_note'],
            cb: () => this.saveCustomData()
        },
        paymentMethod: {
            fields: ['payment.method'],
            cb: () => this.savePaymentMethod()
        }
    };

    private updateCheckoutBasketDataOnSubmitEnd = false;

    /**
     * Set to false if initial gtm events are not needed
     */
    protected emitInitialGtmEvents = true;

    /**
     * Currently visible step
     */
    protected set currentStepName(currentStepName: string | undefined) {
        this.currentStepName$.next(currentStepName);
    }
    protected get currentStepName(): string | undefined {
        return this.currentStepName$.value;
    }

    readonly currentStepName$ = new BehaviorSubject<string | undefined>(this.hashStorageService.get('step'));

    /**
     * If guest checkout is preferred by customer
     */
    @SessionStorage('guestCheckout')
    protected guestCheckout?: boolean;

    /**
     * Flag we use to hold checkout submission when some async processing is required
     */
    continueSubmission?: boolean;

    private passedSubmit = false;

    //////////////////////////////////
    //
    // STEPS
    //
    //////////////////////////////////

    // @ViewChildren(Checkout.IStep)
    // steps !: QueryList<Checkout.IStep>;

    steps: Checkout.ISteps = {};

    /**
     * Ordered steps sequence
     */
    stepsSequence: Checkout.IStep[] = [];

    /**
     * Ordered steps sequence (visible only)
     */
    visibleStepsSequence: Checkout.IStep[] = [];

    saveCustomData = this.timeoutService.debounce(
        (force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) => this._saveCustomData(force, onSuccess, onError),
        1000
    );

    saveShippingAddress = this.timeoutService.debounce(
        (force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) => this._saveShippingAddress(force, onSuccess, onError),
        1000
    );

    saveShippingMethod = this.timeoutService.debounce(
        (
            force = false,
            onSuccess?: IRequestCallback,
            onError?: IRequestCallback,
            status = this.saveShippingMethodStatus,
            data = this.data.shipping
        ) => this._saveShippingMethod(force, onSuccess, onError, status, data),
        1000
    );

    saveBillingAddress = this.timeoutService.debounce(
        (force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) => this._saveBillingAddress(force, onSuccess, onError),
        1000
    );

    savePaymentMethod = this.timeoutService.debounce(
        (force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) => this._savePaymentMethod(force, onSuccess, onError),
        1000
    );

    saveEmail = this.timeoutService.debounce(
        (force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback, data?: Nexus.Api.Checkout.SaveEmail.Request) =>
            this._saveEmail(force, onSuccess, onError, data),
        500
    );

    resetShippingMethod = this.timeoutService.debounce(
        (force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) => this._resetShippingMethod(force, onSuccess, onError),
        500
    );

    protected resetShippingMethodPath: string = Checkout.Endpoints.ResetShippingMethod;

    private checkoutBasketDataNext = this.timeoutService.debounce((onlyOnSubmitEnd: boolean = false) => {
        if (!this.status.busy && !this.confirmationLock && !this.passedSubmit) {
            if (!onlyOnSubmitEnd || this.updateCheckoutBasketDataOnSubmitEnd) {
                this.updateCheckoutBasketDataOnSubmitEnd = false;
                this.checkoutBasketData$.next(this.basket.data);
                this.checkoutBasket.setData(this.basket.data);
            }
        } else {
            this.updateCheckoutBasketDataOnSubmitEnd = true;
        }
    }, 100);

    protected subscriptions = new Subscription();

    onStepsChange = () => this._onStepsChange();

    constructor() {
        const activatedRoute = inject(ActivatedRoute);
        console.log('CheckoutFormComponent constructor', activatedRoute.snapshot);
        // take backend data
        this._defaults = cloneDeep(this.moduleConfig.defaults);
        this.backendData = activatedRoute.snapshot.data['checkoutData'];

        if (this.emitInitialGtmEvents) {
            this.events.broadcast(Checkout.EVENTS.Create, this);
        }
        // needed for samples override
        this.nexusApiService = this.app.request.nexus;
    }

    //////////////////////////////////
    //
    // EVENTS
    //
    //////////////////////////////////

    /**
     * Set up checkout listeners
     */
    ngOnInit() {
        this.setInitStep();

        this.subscriptions.add(
            this.currentStepName$.subscribe(stepName => {
                this.hashStorageService.set('step', stepName);
            })
        );
        // subscribe for updates
        this.subscriptions.add(
            this.app.request.subscribe((requestData: IResponseMeta | undefined) => {
                if (!requestData) {
                    return;
                }
                const { result } = requestData;
                if (result) {
                    if (result.checkout) {
                        this.backendData = result.checkout;
                    }
                    if (result.checkoutPatch) {
                        this.patch(result.checkoutPatch);
                    }
                    if (result.checkoutPatchDirect) {
                        this.patch(result.checkoutPatchDirect, true);
                    }
                }
                this.detectChanges();
            })
        );
        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.StepValidityChange).subscribe(() => {
                this.goToStep(this.currentStep);
                this.detectChanges();
            })
        );

        this.setBasketSubscription();
        [
            this.saveShippingMethodStatus,
            this.saveShippingAddressStatus,
            this.saveCustomDataStatus,
            this.saveBillingAddressStatus,
            this.savePaymentMethodStatus,
            this.resetShippingMethodStatus
        ].forEach(status => {
            this.subscriptions.add(status.subscribe(() => this.detectChanges()));
        });

        this.subscriptions.add(
            this.status.subscribe(() => {
                if (!this.status.busy) {
                    this.checkoutBasketDataNext(true);
                }
                this.detectChanges();
            })
        );

        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SuccessLoadError).subscribe((error: any) => {
                this.status.superBusy = false;
                this.status.error = error;
            })
        );

        if (this.emitInitialGtmEvents) {
            this.events.broadcast<Checkout.IInitEventData>(Checkout.EVENTS.Init, {
                checkout: this,
                currentStep: this.currentStep!,
                currentStepName: this.currentStepName
            });

            // trigger checkout initiation
            this.dfEventService.dispatch({ id: 'dfCheckoutBegin', data: {} });
            this.notifyShippingMethodChange();
            this.notifyPaymentMethodChange();
        }
    }

    /**
     * Cleanup in destroy
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.currentStepName = undefined;
        this.destroyed = true;
        this.events.broadcast<Checkout.IDestroyEventData>(Checkout.EVENTS.Destroy, { checkout: this });
    }

    ngAfterViewInit() {
        this.timeoutService.setTimeout(() => {
            const currentStep = this.currentStep;

            if (currentStep && this.getStepIndex(currentStep) > 0) {
                const stepElement = currentStep.domElement;
                if (stepElement) {
                    this.scrollToService.scrollTo(stepElement, { offset: 80 });
                }
            }
        }, 200);
    }

    /**
     * Subscribes scope/component to checkout changes
     * @param {IObserver} observer Callback
     * @param {any}       scope    scope/component
     */
    subscribe(...args): Subscription {
        return this.subject.subscribe(...args);
    }

    /**
     * Triggers next event that checkout changed
     */
    next(change: Checkout.IStateChange) {
        this.subject.next(change);
        this.events.broadcast(Checkout.EVENTS.Update);
    }

    protected setBasketSubscription() {
        const checkBasket = (init = false) => {
            if (this?.status && !this.passedSubmit) {
                if (this.status.busy || this._confirmationLock) {
                    this.timeoutService.setTimeout(checkBasket, 500);
                    return;
                } else if (!this.basket.items.length) {
                    this.onEmptyBasket();
                }
            }

            this.detectChanges();
        };

        this.subscriptions.add(
            this.basket.subscribe(() => {
                this.checkoutBasketDataNext();
                checkBasket();
            })
        );
        checkBasket(true);
    }

    onEmptyBasket() {
        this.router.navigateByUrl(routeOverride('/basket'));
    }

    onFormDataChange(step: Checkout.EStepName, change: any) {
        this.formDataChange$.next({ step, change });
        this.syncCustomerAddressId(step, change);
        this.fireTriggers();
        this.afterFormDataChange(step, change);

        // make sure that any changes didnt make prev steps invalid
        if (!this._confirmationLock && this.currentStepName) {
            this.checkStep(this.currentStep);
        }
    }

    protected afterFormDataChange(step: Checkout.EStepName, change: any) {}

    protected syncCustomerAddressId(step: Checkout.EStepName, change: any) {
        if (step === 'payment') {
            if (change.customer_address_id) {
                this.data.billing.to_shipping = false;
            } else if (change.to_shipping) {
                this.data.address.billing.customer_address_id = undefined;
            }
        }
    }

    private fireTriggers() {
        const data = cloneDeep(this.data);
        const lastData = this.lastData;

        if (data?.billing?.to_shipping) {
            data.address.billing = data.address.shipping;
        }

        each(this.triggers, setup => {
            if (
                setup.fields.some(field => {
                    const a = get(data, field.split('.'));
                    const b = get(lastData, field.split('.'));
                    // falsy vals like 0 vs undefined we need to compare separately
                    return a !== b && !(!a && !b);
                })
            ) {
                setup.cb();
            }
        });

        this.lastData = cloneDeep(data);

        if (this.lastData?.billing?.to_shipping) {
            this.lastData.address.billing = this.lastData.address.shipping;
        }
    }

    /**
     * Patches backend data
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {any} checkoutPatch Patch data
     * @param  {boolean = false} direct With direct mode we can find deep objects to patch only
     */
    protected patch(checkoutPatch: any, direct = false) {
        this.timeoutService.setTimeout(() => {
            if ((<ViewRef>this._ref).destroyed || this.passedSubmit) {
                return;
            }
            if (direct) {
                each(checkoutPatch, (patch: any, path: string) => {
                    const target = get(this.backendData, path);
                    if (target) {
                        Object.assign(target, patch);
                    }
                });
            } else {
                // patch quote separately - each part may come separately
                if (checkoutPatch.quote) {
                    Object.assign(this.backendData.quote, checkoutPatch.quote);
                    delete checkoutPatch.quote;
                }
                Object.assign(this.backendData, checkoutPatch);
                // broadcast
                if (checkoutPatch.shipping && checkoutPatch.shipping.methods) {
                    this.events.broadcast<Checkout.IShippingMethod[]>(
                        Checkout.EVENTS.ShippingMethodsUpdate,
                        checkoutPatch.shipping.methods
                    );
                }
                if (checkoutPatch.payment && checkoutPatch.payment.methods) {
                    this.events.broadcast<Checkout.IPaymentMethod[]>(Checkout.EVENTS.PaymentMethodsUpdate, checkoutPatch.payment.methods);
                }
                if (checkoutPatch.totals) {
                    this.basket.totals = checkoutPatch.totals;
                    this.events.broadcast<Nexus.Lib.Basket.Totals | undefined>(Checkout.EVENTS.TotalsUpdate, checkoutPatch.totals);
                    this.checkoutBasketDataNext();
                }

                if (checkoutPatch.delivery_date) {
                    this.events.broadcast(Checkout.EVENTS.DeliveryDateUpdate, checkoutPatch.delivery_date);
                }
            }
            this.next({ full: false, patch: checkoutPatch });
        });
    }

    protected toAddressData(address: Checkout.IAddress): IAddressData {
        const customerAddressId = address.customer_address_id;
        if (customerAddressId && this.customer.model.isLoggedIn) {
            const customerAddress = this.customer.addressBook.byId(customerAddressId);
            if (customerAddress) {
                const addressData: Checkout.IAddress = { ...customerAddress.data };
                delete addressData.id;
                addressData.customer_address_id = address.customer_address_id;
                return addressData;
            }
        }
        return address;
    }
    //////////////////////////////////////////
    ///
    /// CHECKOUT DATA
    ///
    //////////////////////////////////////////

    /**
     * Checkout data coming from BE
     * difference between BE data and data is taht data is modified on checkout while BE data can only be changed by response
     */
    get backendData(): Checkout.IData {
        return this._backendData;
    }

    /**
     * Its input as we inject it to component when loding it
     */
    @Input()
    set backendData(backendData: Checkout.IData) {
        this._backendData = backendData;
        // cant laod checkout data - probably empty basket
        if (!backendData) {
            this.onEmptyBasket();
            return;
        }
        if (!this.data || this.data.quote_id !== backendData.quote.quote_id) {
            this.data = mergeWith(
                this.backendData.quote,
                { billing: { to_shipping: !!this.backendData.billing.to_shipping } },
                this.defaults,
                nilMergeDeep
            );

            if (
                (this.data.address.billing.customer_address_id &&
                    this.data.address.shipping?.customer_address_id &&
                    this.data.address.billing.customer_address_id === this.data.address.shipping.customer_address_id) ||
                this.moduleConfig.forceBillingToShipping ||
                this.compareAddresses(this.data.address.billing, this.data.address.shipping)
            ) {
                this.data.billing.to_shipping = true;
            }
            this.lastData = cloneDeep(this.data);
        }

        // reset cache
        this._paymentMethodsAndTokens = undefined;

        this.next({ full: true });
    }

    /**
     * Checks if its free order (with no payment)
     * @return {Boolean}
     */
    get isFree(): boolean {
        return !!(this.basket.data.totals && this.basket.data.totals.grand_total < 0.0001);
    }

    /**
     * Checks if there are only virtual products in the basket (no shipping)
     * @return {Boolean}
     */
    get isVirtual(): boolean {
        return !!this._backendData?.quote.isVirtual;
    }

    /**
     * Provides checkout totals
     */
    get totals(): Nexus.Lib.Basket.Totals | undefined {
        return this.basket.data.totals;
    }

    /**
     * Provides payment methods
     * @return {Checkout.IPaymentMethod[]}
     */
    get paymentMethods(): Checkout.IPaymentMethod[] {
        return (this._backendData?.payment.methods as Checkout.IPaymentMethod[]) || [];
    }

    /**
     * Provides payment methods
     * @return {ICardPaymentMethod[]}
     */
    get paymentTokens(): CreditCard[] {
        return (
            this.app.cache.get(['checkout.paymentTokens', ...this.app.customer.model.cards, this.paymentMethods], () => {
                const tokens = [...(this.app.customer.model.cards || [])];
                if (tokens.length) {
                    const eventData: Checkout.IPaymentTokensBeforeEvent = {
                        tokens,
                        methods: [...this.paymentMethods]
                    };
                    this.events.broadcast<Checkout.IPaymentTokensBeforeEvent>(Checkout.EVENTS.PaymentTokensBefore, eventData);
                    return eventData.tokens;
                } else {
                    return tokens;
                }
            }) || []
        );
    }

    /**
     * Provides payment methods
     * @return {Checkout.IPaymentMethod[]}
     */
    get paymentMethodsAndTokens(): Checkout.IPaymentMethod[] {
        const paymentMethodsAndTokens = union(this.paymentMethods, this.paymentTokens);

        if (this._paymentMethodsAndTokens === undefined || !isEqual(this._paymentMethodsAndTokens, paymentMethodsAndTokens)) {
            this._paymentMethodsAndTokens = paymentMethodsAndTokens;
        }

        return this._paymentMethodsAndTokens;
    }

    /**
     * Provides shipping methods
     * @return {Checkout.IShippingMethod[] }
     */
    get shippingMethods(): Checkout.IShippingMethod[] {
        return this._backendData?.shipping ? this._backendData.shipping.methods || [] : [];
    }

    /**
     * Checks if checkout has any delivery address (not if virtual or C&C)
     * @return {Boolean}
     */
    get hasDeliveryAddress(): boolean {
        return !this.isVirtual && !this.selfCollect && !this.basket.data.is_multishipping;
    }

    /**
     * Provides shipping methods of collection type
     * @return {object[]}
     */
    get collectionShippingMethods(): Checkout.IShippingMethod[] {
        if (!this._backendData?.shipping) {
            return [];
        }
        return this._backendData.shipping.methods.filter((method: Checkout.IShippingMethod) => this.isCollectionMethod(method));
    }

    /**
     * Checks if there is any option for collection type method allowed
     * @return {Boolean}
     */
    get hasCollectionMethods(): boolean {
        return this.collectionShippingMethods.length > 0;
    }

    /**
     * Provides shipping methods of non-collection type
     * @return {object[]}
     */
    get nonCollectionShippingMethods(): Checkout.IShippingMethod[] {
        if (!this._backendData?.shipping) {
            return [];
        }
        const methods = this._backendData.shipping.methods.filter(method => !this.isCollectionMethod(method));
        if (isEqual(this._nonCollectionShippingMethods, methods)) {
            return this._nonCollectionShippingMethods || [];
        }
        this._nonCollectionShippingMethods = methods;
        return this._nonCollectionShippingMethods || [];
    }

    /**
     * Checks if there is any option for non collection type method allowed
     * @return {Boolean}
     */

    get hasNonCollectionMethods(): boolean {
        return this.nonCollectionShippingMethods.length > 0;
    }

    /**
     * Checks if currently picked shipping method is collection type
     * @return {Boolean}
     */
    get selfCollect(): boolean {
        return this.shippingMethodCode ? this.isCollectionMethod(this.shippingMethodCode) : false;
    }

    /**
     * Sets checkout to collection or non collection method
     * @param {boolean} setTo
     */
    set selfCollect(setTo: boolean) {
        const isNow = this.selfCollect;

        if (setTo) {
            if (!isNow) {
                const methods = this.collectionShippingMethods;
                if (methods.length) {
                    this.data.shipping.method = methods[0].code;
                }
            }
        } else {
            if (isNow) {
                this.data.shipping.method = undefined;
            }
            this.selfCollectStoreId = undefined;
        }
    }

    get selfCollectStoreId(): number | undefined {
        return this.data.shipping.additionalData?.store_id || undefined;
    }

    set selfCollectStoreId(id: number | undefined) {
        if (!this.data.shipping.additionalData) {
            this.data.shipping.additionalData = {};
        }
        this.data.shipping.additionalData.store_id = id;
    }

    /**
     * Provides selected shipping method code
     * @return {string}
     */
    get paymentMethodCode(): string | number | undefined {
        return this.data?.payment?.method;
    }

    /**
     * Provides selected shipping method
     * @return {object}
     */
    get paymentMethod(): Checkout.IPaymentMethod | undefined {
        return this.paymentMethodCode ? this.paymentMethodByCode(this.paymentMethodCode) : undefined;
    }

    /**
     * Checks if in frame payment method is selected
     * @return {boolean}
     */
    get inFramePaymentSelected(): boolean {
        const method = this.paymentMethod;
        if (method && method.inFrame) {
            return true;
        }
        return false;
    }

    /**
     * Provides selected card to use to pay
     * @return {object}
     */
    get paymentCard(): CreditCard | undefined {
        return this.paymentMethodCode ? this.paymentCardByCode(this.paymentMethodCode) : undefined;
    }

    /**
     * Provides selected shipping method code
     * @return {string}
     */
    get shippingMethodCode(): string | undefined {
        return this.data ? this.data.shipping.method : undefined;
    }

    /**
     * Provides selected shipping method
     * @return {object}
     */
    get shippingMethod(): Checkout.IShippingMethod | undefined {
        return this.shippingMethodCode ? this.shippingMethodByCode(this.shippingMethodCode) : undefined;
    }

    /**
     * Checks if shippsing method exists
     */
    get hasShippingMethod(): boolean {
        return this.shippingMethod ? true : false;
    }

    /**
     * Provides email address to be used with checkout
     * @return {string}
     */
    get email(): string | undefined {
        return this.app.customer.model.data.email || this.data.account?.email || this.backendData.quote.address.billing['email'];
    }

    /**
     * Checks if client picked billing to shipping
     * @return {bool}
     */
    get isBillingToShipping(): boolean {
        return this.getIsBillingToShipping();
    }

    set isBillingToShipping(value: boolean) {
        this.data.billing.to_shipping = value;
        this.detectChanges();
    }

    getIsBillingToShipping(): boolean {
        return this.data.billing.to_shipping; // && this.hasDeliveryAddress; // that second condition cannot be set!
    }

    /**
     * Provides full billing address
     * @return {object}
     */
    get billingAddress(): IAddressData | undefined {
        return this.toAddressData(
            this.isBillingToShipping && this.hasDeliveryAddress ? this.data.address.shipping : this.data.address.billing
        );
    }

    /**
     * Provides full shipping address
     * @return {object}
     */
    get shippingAddress(): IAddressData | undefined {
        return this.data && this.data.address && this.data.address.shipping && this.toAddressData(this.data.address.shipping);
    }

    /**
     * Checks if gievn method is collection type
     * @return  {boolean}
     */
    isCollectionMethod(method: string | Checkout.IShippingMethod): boolean {
        const codes = this.moduleConfig.collectionMethods;
        const methodCode = typeof method === 'object' ? method.code : method;
        return typeof methodCode === 'string' && codes.includes(methodCode);
    }

    /**
     * Provides selected payment method
     * @return {object}
     */
    paymentMethodByCode(code: string | number): Checkout.IPaymentMethod | undefined {
        return this.paymentMethods.find(method => method.code === code);
    }
    /**
     * Provides selected payment token
     * @return {object}
     */
    paymentCardByCode(code: string | number): CreditCard | undefined {
        const cards = this.app.customer.model.cards;
        if (!cards) {
            return undefined;
        }
        return cards.find(card => card.code === code);
    }

    /**
     * Provides selected shipping method
     * @return {object}
     */
    shippingMethodByCode(code: string): Checkout.IShippingMethod | undefined {
        return this.shippingMethods.find(method => method.code === code);
    }
    //////////////////////////////////////////
    ///
    /// CHECKOUT STATE
    ///
    //////////////////////////////////////////

    /**
     * If checkout is in guest checkout mode
     */
    get isGuestCheckout(): boolean {
        return !!this.guestCheckout && !this.app.customer.model.isLoggedIn;
    }

    get canEdit() {
        return !this._confirmationLock;
    }

    /**
     * Check if all steps in checkout are valid.
     * Used to show additional last step or content if entire checkout is valid.
     * (e.g on OB show 'Review your order' section)
     */
    get isCheckoutValid(): boolean {
        return !Object.values(this.steps).find(i => !i.valid);
    }

    /**
     * Validate checkout (e.g on submit) and scroll to first error if any.
     */
    validateCheckout() {
        return this.checkStep(true, true);
    }

    /**
     * Toggles guesst checkout status
     * @param  {boolean} toState optional status to togle to
     */
    toggleGuestCheckout(toState: boolean): void {
        if (toState === undefined) {
            toState = !this.isGuestCheckout;
        }
        this.guestCheckout = toState;
        this.events.broadcast<Checkout.ICheckoutGuestStateChangedEventData>(Checkout.EVENTS.CheckoutGuestStateChanged, {
            toState,
            checkout: this
        });
        this.detectChanges();
    }

    compareAddresses(address1: Checkout.IAddress, address2: Checkout.IAddress): boolean {
        const fieldsToCheck = ['city', 'country_id', 'firstname', 'lastname', 'postcode', 'street_1', 'street_2', 'telephone'];
        if (address1 && address2) {
            // we check if any field has different value
            return !fieldsToCheck.find(field => {
                const v1 = address1[field];
                const v2 = address2[field];
                if (!v1 && !v2) {
                    // street_2 is not required, can be undefined
                    if (field === 'street_2') {
                        return false;
                    }
                    return true;
                } else {
                    return v1 !== v2;
                }
            });
        }
        return false;
    }

    //////////////////////////////////////////
    ///
    /// save / submit requests
    ///
    //////////////////////////////////////////

    private saveShippingAddressInc = 0;
    /**
     * Updates shipping methods by sending shipping address
     * to native magento2 rest api
     * @return {promise | null}
     */
    _saveShippingAddress(force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        const saveShippingAddressInc = this.saveShippingAddressInc++;

        // send request
        return this.app.request.send(
            this.saveShippingAddressStatus,
            async () => {
                const result = await this.nexusApiService.checkout.saveShippingAddress({
                    billing_to_shipping: this.data.billing.to_shipping,
                    ...this.data.address.shipping
                });
                if (saveShippingAddressInc < this.saveShippingAddressInc) {
                    return result;
                }
            },
            this.addDetectChanges(onSuccess),
            onError
        );
    }

    /**
     * Updates shipping methods by sending shipping address
     * to native magento2 rest api
     * @return {promise | null}
     */
    _saveCustomData(force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback, status = this.saveCustomDataStatus) {
        // send request
        return this.app.request.send(
            status,
            () => this.nexusApiService.checkout.saveCustom(this.data.custom!),
            this.addDetectChanges(onSuccess),
            onError
        );
    }

    private saveShippingMethodInc = 0;

    /**
     * Saves shipping method
     */
    _saveShippingMethod(
        force = false,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback,
        status = this.saveShippingMethodStatus,
        data = this.data.shipping
    ): Promise<IRequestCallback | undefined> | undefined {
        // send request
        if (data && data.method && this.canSaveShippingMethod(data)) {
            const eventData: Checkout.ISaveShippingMethodEventData = { ...data, methodDetails: this.shippingMethod };

            if (
                data.delivery_date &&
                this.moduleConfig.nominatedDayMethods &&
                !this.moduleConfig.nominatedDayMethods.includes(data.method)
            ) {
                data.delivery_date = false; // we need to send false to BE when we dont have nominated day method selected to reset it
            }

            this.events.broadcast<Checkout.ISaveShippingMethodEventData>(Checkout.EVENTS.SaveShippingMethodBefore, eventData);

            const saveData: Checkout.ISaveShippingMethodData = omit(data, 'methodDetails'); // we only have methodDetails for internal processing, we need to take from event to include patches

            const saveShippingMethodInc = this.saveShippingMethodInc++;
            return this.app.request.send(
                status,
                async () => {
                    const result = await this.nexusApiService.checkout.saveShippingMethod(this.getSaveShippingMethodData(saveData));
                    if (saveShippingMethodInc < this.saveShippingMethodInc) {
                        return result;
                    }
                },
                () => {
                    // this is needed cause BE does not provide full patch
                    this.backendData.quote!.shipping.method = data.method as string;
                    this.notifyShippingMethodChange();
                    this.addDetectChanges(onSuccess);
                },
                onError
            );
        }

        return undefined;
    }

    protected canSaveShippingMethod(data: Checkout.IQuoteDataShipping): boolean {
        // fix store collection save without `store_id`
        if (data.method && this.isCollectionMethod(data.method)) {
            return !!data.additionalData?.store_id || !!data.additionalData?.booking_code;
        }

        return true;
    }

    private notifyShippingMethodChange() {
        const shippginMethod = this.backendData.quote!.shipping.method;
        if (shippginMethod && this.lastShippginMethodNotified !== shippginMethod) {
            this.lastShippginMethodNotified = shippginMethod;
            this.dfEventService.dispatch({
                id: 'dfCheckoutChange',
                data: {
                    type: 'shippingMethod',
                    quote: this.backendData as Nexus.Api.Checkout.Load.Response['checkout']
                }
            });
        }
    }

    private notifyPaymentMethodChange() {
        const paymentMethod = this.backendData.quote!.payment.method as string;
        if (paymentMethod && this.lastPaymentMethodNotified !== paymentMethod) {
            this.lastPaymentMethodNotified = paymentMethod;
            this.dfEventService.dispatch({
                id: 'dfCheckoutChange',
                data: {
                    type: 'paymentMethod',
                    quote: this.backendData as Nexus.Api.Checkout.Load.Response['checkout']
                }
            });
        }
    }

    /**
     * Returns data to be sent to server when saving shipping method.
     */
    protected getSaveShippingMethodData(data: Checkout.IQuoteDataShipping): Checkout.ISaveShippingMethodData {
        return data;
    }

    /**
     * Updates payment methods by sending billing address
     * to native magento2 rest api
     * @return {promise | null}
     */
    _saveBillingAddress(force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        // send request

        const email = this.email;
        const billingAddress = { ...this.billingAddress, id: this.data.address.billing.id };
        if (email && billingAddress) {
            const data: Checkout.ISaveBillingAddressData = {
                email,
                billing_to_shipping: this.data.billing.to_shipping,
                billing_address: billingAddress
            };

            return this.app.request.send(
                this.saveBillingAddressStatus,
                () => this.nexusApiService.checkout.saveBillingAddress(data),
                this.addDetectChanges(onSuccess),
                onError
            );
        }
        return undefined;
    }

    _savePaymentMethod(force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        // send request
        if (this.data.payment && this.data.payment.method) {
            const eventData: Checkout.ISavePaymentMethodEventData = { ...this.data.payment, ver: 2, methodDetails: this.paymentMethod };

            this.events.broadcast<Checkout.ISavePaymentMethodEventData>(Checkout.EVENTS.SavePaymentMethodBefore, eventData);

            const data: Checkout.ISavePaymentMethodData = omit(eventData, 'methodDetails'); // we only have methodDetails for internal processing, we need to take from event to include patches

            if (data.method) {
                return this.app.request.send(
                    this.savePaymentMethodStatus,
                    () => this.nexusApiService.checkout.savePayment(data),
                    () => {
                        // this is needed cause BE does not provide full patch
                        if (data.method === 'vault' && data.additional_data?.public_hash) {
                            this.backendData.quote!.payment.method = data.additional_data.public_hash;
                        } else if (data.additional_data?.cardId) {
                            this.backendData.quote!.payment.method = data.additional_data.cardId;
                        } else {
                            this.backendData.quote!.payment.method = data.method as string;
                            if (this.backendData.quote!.payment?.additional_data?.public_hash) {
                                delete this.backendData.quote!.payment.additional_data.public_hash;
                            }
                            if (this.backendData.quote?.payment?.additional_data?.cardId) {
                                delete this.backendData.quote!.payment.additional_data.cardId;
                            }
                        }

                        this.notifyPaymentMethodChange();
                        this.addDetectChanges(onSuccess);
                    },
                    this.addDetectChanges(
                        compact(
                            flatten([
                                () => {
                                    this.data.payment.method = undefined;
                                },
                                onError
                            ])
                        )
                    )
                );
            }
        }
        return undefined;
    }

    /**
     * Saves customer email address
     */
    _saveEmail(force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback, data = this.getSaveEmailData()) {
        // send request
        if (this.email) {
            return this.app.request.send(
                this.saveEmailStatus,
                () => this.nexusApiService.checkout.saveEmail(data),
                this.addDetectChanges(onSuccess),
                onError
            );
        }
        return undefined;
    }

    protected getSaveEmailData(): Nexus.Api.Checkout.SaveEmail.Request {
        return {
            email: this.email!
        };
    }

    /**
     * Resets shipping method
     */
    _resetShippingMethod(force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        // send request
        return this.app.request.send(
            this.resetShippingMethodStatus,
            {
                path: this.resetShippingMethodPath,
                data: undefined
            },
            this.addDetectChanges(onSuccess),
            onError
        );
    }

    protected addDetectChanges(cb?: IRequestCallback): IRequestCallback {
        const dc = () => this.detectChanges();
        if (!cb) {
            return dc;
        }
        if (Array.isArray(cb)) {
            return [...cb, dc];
        }
        return [cb, dc];
    }

    getSubmissionData(): Checkout.ISubmissionData {
        return {
            quote_id: this.backendData.quote.quote_id,
            billing_address: { ...this.billingAddress, id: this.data.address.billing.id },
            shipping_address: this.isVirtual ? undefined : this.shippingAddress,
            email: this.email,
            newsletter: this.data.newsletter ? true : undefined,
            source: this.data.source,
            paymentMethod: {
                additional_data: omit(this.data.payment, 'method'),
                method: this.data.payment.method
            },
            custom: this.data.custom,
            subscriberProperties: this.data.subscriberProperties
        };
    }

    submit() {
        return this._submit(this.getSubmissionData());
    }

    submitWithData(
        data: Checkout.ISubmissionData,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallbackFunction,
        force = false
    ) {
        return this._submit(data, onSuccess, onError, force);
    }

    /**
     * Submits checkout data
     *
     * @return {promise|void} If save can't be run now returns void, otherwise returns save request promise
     */
    private _submit(
        data: Checkout.ISubmissionData,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallbackFunction,
        force = false
    ) {
        // make sure step is valid before doing anything
        const successCallbacks = [onSuccess].filter(i => !!i);
        if (this.validateCheckout() || force) {
            const next = () => {
                return this.app.request.send(
                    this.status,
                    () =>
                        new Promise<Checkout.ISubmissionResponse>((resolve, reject) => {
                            // eslint-disable-next-line @typescript-eslint/no-this-alias
                            const that = this;
                            let tryNum = 0;

                            _send();

                            function _send() {
                                tryNum++;

                                if (tryNum > 10) {
                                    reject({
                                        error: `Your submission is still being processed,
                                                please try to submit in a moment to get confirmation.`
                                    });
                                    that.status.superBusy = false;
                                    return;
                                }

                                // buffer basket data so we can pass them with on success event
                                const basketData = that.basket.data;

                                that.app.request.send(
                                    undefined,
                                    () => that.nexusApiService.checkout.submit(data),
                                    compact([
                                        ...successCallbacks,
                                        (result: Checkout.ISubmissionResponse | undefined) => {
                                            if (!result) {
                                                return;
                                            }
                                            that.passedSubmit = true;

                                            const eventDdata: Checkout.ISubmitSuccessEventData = {
                                                request: data,
                                                basket: basketData,
                                                response: result
                                            };

                                            that.events.broadcast<Checkout.ISubmitSuccessEventData>(
                                                Checkout.EVENTS.SubmitSuccess,
                                                eventDdata
                                            );

                                            if (!result.post_redirect && !result.external_redirect && !result.preventSuccess) {
                                                // keep subbmission as busy, we are redirecting now
                                                // (that will prevent empty basket redirect)
                                                that.status.superBusy = true;
                                                that.redirectToSuccess();
                                            } else if (result.external_redirect) {
                                                that.status.superBusy = true;
                                            }
                                        }
                                    ]),
                                    result => {
                                        switch (result.error) {
                                            case Checkout.Errors.RedirectCart:
                                                that.lastStepName = undefined;
                                                that.router.navigateByUrl(routeOverride('/basket'));
                                                break;
                                            case 504:
                                                that.status.superBusy = true;
                                                that.status.error = `Connection timed out, but it may be processed already.
                                                         Please hold while we retry.`;

                                                that.timeoutService.setTimeout(_send, 1000);
                                                return;
                                            case 'SUBMITWAIT':
                                                that.status.superBusy = true;
                                                that.status.error = `Your checkout submission is being processed already.
                                                    Please hold a while for confirmation.`;

                                                that.timeoutService.setTimeout(_send, 1000);
                                                return;
                                        }
                                        that.status.superBusy = false;

                                        const eventData: Checkout.ISubmitErrorEventData = {
                                            request: data,
                                            basket: basketData,
                                            error: result.error,
                                            data_error: result?.data_error
                                        };

                                        that.events.broadcast<Checkout.ISubmitErrorEventData>(Checkout.EVENTS.SubmitError, eventData);
                                        reject(result);
                                    }
                                );
                            }
                        }),
                    undefined,
                    onError
                );
            };

            const event: Checkout.ISubmitBeforeEventData = {
                continueSubmission: true,
                checkout: this,
                data,
                checkoutData: this.data,
                next,
                onSuccess: successCallbacks
            };
            // run submission request
            this.events.broadcast<Checkout.ISubmitBeforeEventData>(Checkout.EVENTS.SubmitBefore, event);

            // if continueSubmission flag was changed in listener then we will leave further processing to that
            if (event.continueSubmission) {
                next();
            }
        }
    }

    redirectToSuccess() {
        this.lastStepName = undefined;
        this.router.navigate(['/checkout/success']);
    }

    //////////////////////////////////////////
    ///
    /// Steps features
    ///
    //////////////////////////////////////////

    get lastStepName(): string | undefined {
        return this.checkout.lastStepName;
    }

    set lastStepName(value: string | undefined) {
        this.checkout.lastStepName = value;
    }

    /**
     * Locks checkout on confirmation step
     * @param {Checkout.IStep} step
     */
    set confirmationLock(step: Checkout.IStep) {
        this._confirmationLock = step;
        step.goTo();
        if (!step) {
            this.checkoutBasketDataNext(true);
        }
    }

    get confirmationLock(): Checkout.IStep | undefined {
        return this._confirmationLock;
    }

    /**
     * Triggered when steps array changes
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */

    registerStep(step: Checkout.IStep) {
        this.steps[step.name] = step;
        this.onStepsChange();
    }

    unregisterStep(step: Checkout.IStep) {
        delete this.steps[step.name];
        this.onStepsChange();
    }

    /**
     * Called when steps change
     */
    _onStepsChange() {
        // there were some problems with debauce here, but it seems to be fine now
        if (!this.destroyed) {
            this.stepsSequence = sortBy(Object.values(this.steps), 'order');
            this.visibleStepsSequence = this.stepsSequence.filter(i => !i.hidden);

            this.events.broadcast<Checkout.IStepsSetUpdateEventData>(Checkout.EVENTS.StepsSetUpdate, {
                steps: this.steps,
                checkout: this
            });
            this.detectChanges();
        }
        if (this.emitInitialGtmEvents) {
            this.triggerStepInitEvent();
        }
    }

    /**
     * This should be invoked whenever a new checkout step is added to the checkout flow,
     * but this will trigger the event only after all steps are added.
     * @private
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private triggerStepInitEvent = this.timeoutService.debounce(
        () =>
            this.events.broadcast<Checkout.IInitEventData>(Checkout.EVENTS.InitAfter, {
                checkout: this,
                currentStep: this.currentStep!,
                currentStepName: this.currentStep?.name
            }),
        200
    );

    detectChanges() {
        if (!(<ViewRef>this._ref).destroyed) {
            this._ref.detectChanges();
        }
    }

    getNextStep(vsStep: Checkout.IStep | string): Checkout.IStep | undefined {
        const order = this.toStep(vsStep)?.order;
        if (order === undefined) {
            return undefined;
        }
        return this.stepsSequence.find(i => i.order > order);
    }

    goToNextStep(vsStep?: Checkout.IStep | string) {
        const nextStep = vsStep ? this.getNextStep(vsStep) : this.nextStep;
        if (nextStep) {
            this.goToStep(nextStep);
        }
    }

    goToStep(step: string | Checkout.IStep | undefined): void {
        step = this.toStep(step);

        if (!step) {
            return;
        }

        // do not allow leaving confirmation step
        if (this._confirmationLock) {
            step = this._confirmationLock;
        } else if (step.isConfirmation) {
            // only allow confirmation steps if lock is set
            step = last(this.visibleStepsSequence);
        }

        each(this.steps, i => {
            if (i !== step) {
                i.markUnsubmitted();
            }
        });

        const stepBeforeChange = this.currentStep;
        if (step) {
            if (this._confirmationLock || this.checkStep(<Checkout.IStep>step, true)) {
                const stepName = (<Checkout.IStep>step).name;
                if (this.currentStepName === stepName) {
                    return;
                }
                this.currentStepName = stepName;
            }
        } else {
            this.logger.error('Trying to go to not existing step');
        }

        // if step changed (from other one)
        if (
            (stepBeforeChange?.name && stepBeforeChange.name !== this.currentStepName) ||
            (this._confirmationLock && stepBeforeChange !== this._confirmationLock)
        ) {
            Object.values(this.triggers).forEach(item => {
                if (item.steps) {
                    item.cb();
                }
            });
            if (!this.currentStep) {
                return;
            }
            // this notifies about step change (not that its set, only triggered when there was different step before)
            this.events.broadcast<Checkout.IStepChangeEventData>(Checkout.EVENTS.StepChange, {
                stepName: this.currentStep.name,
                step: this.currentStep,
                checkout: this,
                stepBeforeChange: stepBeforeChange?.name
            });

            this.detectChanges();

            this.scrollToStepSection();
        }
    }

    scrollToStepSection() {
        // make sure step is properly visible when changing
        this.timeoutService.setTimeout(() => {
            const currentStep = this.currentStep;
            if (currentStep) {
                const stepElement = currentStep.domElement;
                if (stepElement) {
                    //always scroll to 3ds confirmation
                    if (currentStep.isConfirmation || this.isScrollToElementRequired(stepElement)) {
                        this.scrollToService.scrollTo(stepElement, this.getToCurrentStepScrollOptions());
                        this.timeoutService.setTimeout(() => {
                            if (this.isScrollToElementRequired(stepElement)) {
                                this.scrollToService.scrollTo(stepElement, this.getToCurrentStepScrollOptions());
                            }
                        }, 50);
                    }
                }
            }
        }, this.getScrollToCurrentStepDelay()); // give it a moment to appear
    }

    isScrollToElementRequired(element: HTMLElement): boolean {
        // if top part of step is not in viewport scroll to it
        // when on SM sizes and stepElement is below 60% of viewport height scroll to it anyway
        return !isInViewport(element, 0, 'top') || (this.client.sizeId < 2 && !isInViewport(element, 0.6, 'y'));
    }

    protected getScrollToCurrentStepDelay(): number {
        return 200;
    }

    protected getToCurrentStepScrollOptions(): IScrollToOptions {
        return {};
    }

    /**
     * Checks all steps before given one
     * If true value passed as `step` will check all steps
     */
    checkStep(step: Checkout.IStep | boolean = this.currentStep!, updateForms = false): boolean {
        if (step) {
            let firstInvalid: Checkout.IStep | undefined;
            if (step === true) {
                firstInvalid = this.visibleStepsSequence.find(i => i && !i.valid);
            } else {
                firstInvalid = this.visibleStepsSequence.find(i => i && i.order < (<Checkout.IStep>step).order && !i.valid);
            }
            if (firstInvalid) {
                this.onCheckStepFailure(firstInvalid, updateForms);
                return false;
            }
        }
        return true;
    }

    protected onCheckStepFailure(firstInvalid: Checkout.IStep, updateForms: boolean): void {
        this.currentStepName = firstInvalid.name;
        if (updateForms) {
            firstInvalid.markSubmitted();
        }
    }

    get currentStep(): Checkout.IStep | undefined {
        return (this.currentStepName && this.steps[this.currentStepName]) || first(this.stepsSequence);
    }

    get currentStepIndex(): number {
        return this.currentStep ? this.getStepIndex(this.currentStep) : -1;
    }

    getStepIndex(_step: Checkout.IStep | string): number {
        const step = this.toStep(_step);
        return findIndex(this.stepsSequence, i => i === step);
    }

    get nextStep() {
        return this.currentStep ? this.getNextStep(this.currentStep) : undefined;
    }

    /**
     * Returns checkout step by name
     * @param {string} name
     * @returns {Checkout.IStep | undefined}
     */
    getStepByName(name: string): Checkout.IStep | undefined {
        return this.steps[name];
    }

    toStep(step: string | Checkout.IStep | undefined): Checkout.IStep | undefined {
        return typeof step === 'string' ? this.getStepByName(step) : step;
    }

    /**
     * Tracking function used with checkout steps
     */
    stepTrackFn(index: number, step: Checkout.IStep): string {
        return step.name;
    }

    resetData(): void {
        const keys = [
            'firstname',
            'lastname',
            'city',
            'postcode',
            'street_1',
            'street_2',
            'street_3',
            'telephone',
            'telephone2',
            'country_id',
            'country',
            'region_id',
            'region'
        ];

        keys.forEach(key => {
            delete this.data.address.billing[key];
            delete this.data.address.shipping[key];
        });

        this.detectChanges();
    }

    protected setInitStep(): void {
        // if no step in url we restore last step from session storage
        if (this.moduleConfig.restoreLastStep && !this.currentStepName && this.lastStepName) {
            this.currentStepName = this.lastStepName;
        }
        if (this.currentStepName === 'checkout_confirmation_iframe') {
            this.currentStepName = 'payment';
        }
    }
}
